import * as React from 'react';
import { useBreakpoint, type AtLeast } from '@resi-media/resi-ui';
import { produce } from 'immer';
import qs from 'qs';
import { useSearchParams } from 'react-router';
import { createContainer } from 'react-tracked';
import useSWR from 'swr';
import { match } from 'ts-pattern';
import { STORAGE_TYPES } from '@sites/constants/generic';
import {
  FEATURE_VIDEOS_DESKTOP,
  FEATURE_VIDEOS_MOBILE,
  FEATURE_VIDEOS_TABLET,
  LAYOUT_CONTAINER_ID,
  ROWS_PER_PAGE,
} from '@sites/constants/layout';
import { useQueryParams } from '@sites/contexts/queryContext';
import type CustomError from '@sites/helpers/customError';
import getStorage from '@sites/helpers/getStorage';
import { useTileColumnCount } from '@sites/hooks/useTileColumnCount';
import type { Sites } from '@sites/types';
import { useEnvironment } from '../environmentContext';
import { useFeed } from '../feedContext';

enum SEARCH_CONTEXT_ACTION_TYPES {
  SET_PAGE,
  SET_QUERIES,
  SET_QUERY,
  SET_SEARCH,
  SET_SEARCH_DATA,
  SET_SEARCH_QUERIES,
  SET_SUGGESTIONS,
  SET_SEARCH_INPUT_EXPANDED,
  SET_SEARCH_POPOVER_OPEN,
}

type SearchContextState = {
  isSearchInputExpanded: boolean;
  isSearchPopoverOpen: boolean;
  offset: number;
  page: number;
  queries: Set<string>;
  query: string;
  search: string;
  size: number;
  suggestions: Map<string, Sites.Get.Suggestions>;
  total: number;
  videos: Map<string, Sites.Get.SearchVideo>;
};

type SearchAction =
  | {
      payload: {
        offset: number;
        size: number;
      };
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_QUERIES;
    }
  | {
      payload: {
        query: string;
        shouldReset: boolean;
        size: number;
      };
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH;
    }
  | {
      payload: {
        query: string;
        suggestions: Sites.Get.Suggestions;
      };
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_SUGGESTIONS;
    }
  | {
      payload: boolean;
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_INPUT_EXPANDED;
    }
  | {
      payload: boolean;
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_POPOVER_OPEN;
    }
  | {
      payload: number;
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_PAGE;
    }
  | {
      payload: Set<string>;
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_QUERIES;
    }
  | {
      payload: Sites.Get.Search;
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_DATA;
    }
  | {
      payload: string;
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_QUERY;
    };

const searchContextReducer = (state: SearchContextState, action: SearchAction) => {
  switch (action.type) {
    case SEARCH_CONTEXT_ACTION_TYPES.SET_SUGGESTIONS:
      state.suggestions = state.suggestions.set(action.payload.query, action.payload.suggestions);
      break;
    case SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_DATA:
      state.total = action.payload.totalHits;
      action.payload.videos.forEach((video) => state.videos.set(video.id, video));
      break;
    case SEARCH_CONTEXT_ACTION_TYPES.SET_QUERIES:
      state.queries = action.payload;
      break;
    case SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH:
      state.search = action.payload.query;
      state.query = action.payload.query;
      state.queries = new Set([action.payload.query, ...state.queries]);
      state.size = action.payload.size;

      if (action.payload.shouldReset) {
        state.offset = 0;
        state.total = 0;
        state.page = 1;
        state.videos = new Map<string, Sites.Get.SearchVideo>();
      }
      break;
    case SEARCH_CONTEXT_ACTION_TYPES.SET_QUERY:
      state.query = action.payload;
      break;
    case SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_QUERIES:
      state.offset = action.payload.offset;
      state.size = action.payload.size;
      break;
    case SEARCH_CONTEXT_ACTION_TYPES.SET_PAGE:
      state.page = action.payload;
      break;
    case SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_INPUT_EXPANDED:
      state.isSearchInputExpanded = action.payload;
      break;
    case SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_POPOVER_OPEN:
      state.isSearchPopoverOpen = action.payload;
      break;
  }
};

type SearchContextHook = Omit<
  AtLeast<SearchContextState, 'offset' | 'page' | 'queries' | 'query' | 'size'>,
  'suggestions'
> & {
  isSearchInputExpanded: boolean;
  isSearchPopoverOpen: boolean;
  max: number;
  min: number;
  removePastQuery: (query: string) => void;
  results: {
    error?: CustomError;
    isLoading: boolean;
    total: number;
    videos: Sites.Get.SearchVideo[];
  };
  setPage: (page: number) => void;
  setQueryParams: (queryParams: string) => void;
  setSearch: (queryParams: string | { isSearchTag?: boolean; query: string }) => void;
  setSearchInputExpanded: (isExpanded: boolean) => void;
  setSearchPopoverOpen: (isOpen: boolean) => void;
  setVideoPaginationQueries: ({ offset, size }: { offset: number; size: number }) => void;
  suggestion?: Sites.Get.Suggestions;
  suggestions: {
    data: Map<string, Sites.Get.Suggestions>;
    error?: CustomError;
    isLoading: boolean;
  };
};

const curriedReducerFunction = produce(searchContextReducer);

const PREFETCH_MULTIPLIER = 2;

const useValues = (): readonly [SearchContextHook, () => void] => {
  const { resiStorageKey, SITES_API_V1 } = useEnvironment();
  const { site, tokenData } = useFeed();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isTag } = useQueryParams();
  const columns = useTileColumnCount();
  const mediaQuery = useBreakpoint();
  const customerSite = window.location.pathname.split('/')[1];
  const STORAGE_QUERY_KEY = `${resiStorageKey}-q-${customerSite || 'default'}`;
  const baseVideosPerPage = columns * ROWS_PER_PAGE;
  const videosPerPage = match(mediaQuery)
    .with({ xl: true }, () => baseVideosPerPage * PREFETCH_MULTIPLIER + FEATURE_VIDEOS_DESKTOP)
    .with({ lg: true, xl: false }, () => baseVideosPerPage * PREFETCH_MULTIPLIER + FEATURE_VIDEOS_TABLET)
    .otherwise(() => baseVideosPerPage * PREFETCH_MULTIPLIER + FEATURE_VIDEOS_MOBILE);
  const storage = getStorage(STORAGE_TYPES.LOCAL_STORAGE);

  const [
    {
      isSearchInputExpanded,
      isSearchPopoverOpen,
      offset,
      page,
      queries,
      query,
      search,
      size,
      suggestions,
      total,
      videos,
    },
    dispatch,
  ] = React.useReducer(
    curriedReducerFunction,
    {
      suggestions: new Map(),
      page: 1,
      queries: new Set<string>(),
      query: '',
      search: '',
      size: videosPerPage,
      offset: 0,
      total: 0,
      videos: new Map<string, Sites.Get.SearchVideo>(),
      isSearchInputExpanded: false,
      isSearchPopoverOpen: false,
    },
    (initialState): SearchContextState => {
      const initialQueries = storage.getItem(STORAGE_QUERY_KEY);
      const defaultQueries = initialQueries ? new Set([...JSON.parse(initialQueries)]) : initialState.queries;
      const defaultSearchTerm = searchParams.get('q') ?? initialState.query;

      if (defaultSearchTerm) {
        defaultQueries.add(defaultSearchTerm);
        storage.setItem(STORAGE_QUERY_KEY, JSON.stringify([...defaultQueries]));
      }

      return {
        ...initialState,
        isSearchInputExpanded: Boolean(defaultSearchTerm),
        isSearchPopoverOpen: false,
        queries: defaultQueries,
        query: defaultSearchTerm,
        search: defaultSearchTerm,
      };
    }
  );
  const suggestionsUrl = `${SITES_API_V1}/sites/${site?.id}/videos/suggestions?query=${encodeURIComponent(query)}`;
  const { data, error, isLoading } = useSWR<{ suggestions: Sites.Get.Suggestions }, CustomError>(
    tokenData?.jwt && site?.id && query.trim() ? [suggestionsUrl, tokenData.jwt, query, site.id] : null
  );

  const searchUrl = React.useMemo(() => {
    const params = qs.stringify(
      {
        [isTag ? 'tags' : 'query']: search,
        size,
        offset,
        highlightOptions: {
          numFragments: mediaQuery.lg ? 2 : 1,
          fragmentSize: mediaQuery.lg ? 100 : 50,
        },
      },
      {
        encode: true,
        allowDots: true,
      }
    );
    return `${SITES_API_V1}/sites/${site?.id}/videos?${params}`;
  }, [SITES_API_V1, isTag, mediaQuery.lg, offset, search, site?.id, size]);

  const {
    data: searchData,
    error: searchDataError,
    isLoading: searchDataIsLoading,
  } = useSWR<Sites.Get.Search, CustomError>(
    tokenData?.jwt && site?.id && search ? [searchUrl, tokenData.jwt, search, site.id] : null,
    null,
    { revalidateOnFocus: false }
  );

  // Scroll to top on page change
  React.useEffect(() => {
    const container = document.getElementById(LAYOUT_CONTAINER_ID);
    if (container) {
      container.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [page]);

  React.useEffect(() => {
    if (data) {
      dispatch({
        payload: { query, suggestions: data.suggestions },
        type: SEARCH_CONTEXT_ACTION_TYPES.SET_SUGGESTIONS,
      });
    }
  }, [data, query]);

  React.useEffect(() => {
    if (searchData) {
      dispatch({
        payload: searchData,
        type: SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_DATA,
      });
    }
  }, [searchData]);

  const removePastQuery = React.useCallback(
    (queryParams: string) => {
      const mutableQueries = new Set(queries);
      mutableQueries.delete(queryParams);
      storage.setItem(STORAGE_QUERY_KEY, JSON.stringify([...mutableQueries]));
      dispatch({
        payload: mutableQueries,
        type: SEARCH_CONTEXT_ACTION_TYPES.SET_QUERIES,
      });
    },
    [STORAGE_QUERY_KEY, queries, storage]
  );

  const handleSubmitQueryParam = React.useCallback(
    ({ isSearchTag, queryParam }: { isSearchTag?: boolean; queryParam: string }) => {
      if (queryParam === '') {
        searchParams.delete('q');
        if (!isSearchTag) {
          searchParams.delete('isTag');
        }
      } else {
        searchParams.set('q', queryParam);
        if (isSearchTag) {
          searchParams.set('isTag', 'true');
        }
        if (!queries.has(queryParam)) {
          storage.setItem(STORAGE_QUERY_KEY, JSON.stringify([...queries, queryParam]));
        }
      }

      setSearchParams(searchParams, { replace: true });
    },
    [STORAGE_QUERY_KEY, queries, searchParams, setSearchParams, storage]
  );

  const setQueryParams = React.useCallback((queryParams: string) => {
    dispatch({
      payload: queryParams,
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_QUERY,
    });
  }, []);

  const setSearch = React.useCallback(
    (queryParams: string | { isSearchTag?: boolean; query: string }) => {
      const queryParamToSet =
        typeof queryParams === 'string'
          ? { queryParam: queryParams, isSearchTag: false }
          : { queryParam: queryParams.query, isSearchTag: queryParams.isSearchTag };
      const shouldReset = queryParamToSet.queryParam !== search || isTag !== Boolean(queryParamToSet.isSearchTag);
      handleSubmitQueryParam(queryParamToSet);

      dispatch({
        payload: {
          query: queryParamToSet.queryParam,
          size: videosPerPage,
          shouldReset,
        },
        type: SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH,
      });
    },
    [handleSubmitQueryParam, isTag, search, videosPerPage]
  );

  const setPage = React.useCallback((currentPage: number) => {
    dispatch({
      payload: currentPage,
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_PAGE,
    });
  }, []);

  const setVideoPaginationQueries = React.useCallback((queryParams: { offset: number; size: number }) => {
    dispatch({
      payload: queryParams,
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_QUERIES,
    });
  }, []);

  const setSearchInputExpanded = React.useCallback((isExpanded: boolean) => {
    dispatch({
      payload: isExpanded,
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_INPUT_EXPANDED,
    });
  }, []);

  const setSearchPopoverOpen = React.useCallback((isOpen: boolean) => {
    dispatch({
      payload: isOpen,
      type: SEARCH_CONTEXT_ACTION_TYPES.SET_SEARCH_POPOVER_OPEN,
    });
  }, []);

  const basePageSize = columns * ROWS_PER_PAGE;
  const perPageSize = basePageSize * page;
  const featuredVideosCount = match(mediaQuery)
    .with({ xl: true }, () => FEATURE_VIDEOS_DESKTOP)
    .with({ lg: true, xl: false }, () => FEATURE_VIDEOS_TABLET)
    .otherwise(() => FEATURE_VIDEOS_MOBILE);
  const isLastPage = perPageSize + featuredVideosCount >= total;

  const searchHook = React.useMemo(() => {
    return {
      results: {
        total,
        error: searchDataError,
        videos: [...videos.values()],
        isLoading: searchDataIsLoading,
      },
      suggestions: {
        error,
        data: suggestions,
        isLoading,
      },
      error,
      removePastQuery,
      setQueryParams,
      setSearch,
      setVideoPaginationQueries,
      offset,
      queries,
      query,
      search,
      setPage,
      size,
      page,
      max: isLastPage ? total : perPageSize + featuredVideosCount,
      min: page === 1 ? 1 : perPageSize - basePageSize + featuredVideosCount + 1,
      suggestion: suggestions.get(query),
      isSearchInputExpanded,
      isSearchPopoverOpen,
      setSearchInputExpanded,
      setSearchPopoverOpen,
    };
  }, [
    total,
    searchDataError,
    videos,
    searchDataIsLoading,
    error,
    suggestions,
    isLoading,
    removePastQuery,
    setQueryParams,
    setSearch,
    setVideoPaginationQueries,
    offset,
    queries,
    query,
    search,
    setPage,
    size,
    page,
    isLastPage,
    perPageSize,
    featuredVideosCount,
    basePageSize,
    isSearchInputExpanded,
    isSearchPopoverOpen,
    setSearchInputExpanded,
    setSearchPopoverOpen,
  ]);

  return [
    searchHook,
    () => {
      throw new Error('use functions in the state');
    },
  ];
};

const {
  Provider: SearchProvider,
  useTrackedState: useSearch,
  useUpdate: useSearchUpdate,
} = createContainer<SearchContextHook, () => void, { children: React.ReactNode }>(useValues);

export { SearchProvider, useSearch, useSearchUpdate };
export type { SearchContextHook };
